import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../sections/Footer";

const SECTIONS = [
  {
    h: "1. Information We Collect",
    p: [
      "We collect information you voluntarily provide when you contact us, complete an intake through our portal at trust.igcfirm.com, or request a review of an existing trust. This may include your name, email address, phone number, mailing address, information about your assets and beneficiaries, and, where required for trust preparation, government identifiers such as Social Security Numbers.",
      "We also collect limited technical information automatically when you visit this website, including device type, browser type, pages viewed, and general location, through cookies and analytics tools such as the Facebook pixel and similar tracking technologies.",
    ],
  },
  {
    h: "2. How We Use Your Information",
    p: [
      "We use your information to prepare and deliver the services you request, including living trust setup, asset protection structuring, and legal review coordination; to communicate with you about your file, including the personal call you receive before anything finalizes; to improve our website and services; and to comply with legal and regulatory obligations.",
      "We do not sell your personal information. We do not share your information with third parties for their own marketing purposes.",
    ],
  },
  {
    h: "3. How We Protect Your Information",
    p: [
      "All personal information you submit, including Social Security Numbers and financial details, is protected with military grade AES 256 GCM encryption in transit and at rest. Access to client files is restricted to personnel directly involved in preparing your documents and to the licensed attorneys performing legal review.",
      "While no method of transmission or storage is completely secure, we maintain administrative, technical, and physical safeguards designed to protect your information against unauthorized access, disclosure, alteration, or destruction.",
    ],
  },
  {
    h: "4. Sharing of Information",
    p: [
      "We share your information only as needed to deliver your services: with licensed attorneys performing legal review of your file, with service providers who help operate our portal and communications under confidentiality obligations, and where required by law, court order, or to protect our legal rights.",
    ],
  },
  {
    h: "5. Your Rights",
    p: [
      "You have the right to access the personal information we hold about you, request that it be corrected or updated, request deletion of your information where it is no longer needed for your file or required by law, and opt out of marketing communications at any time.",
      "To exercise any of these rights, contact us at support@igcfirm.com. We will respond to verified requests within a reasonable timeframe.",
    ],
  },
  {
    h: "6. Cookies and Tracking",
    p: [
      "This website uses cookies and pixels, including the Facebook pixel, to measure site performance and advertising effectiveness. You can control cookies through your browser settings and opt out of interest-based advertising through the tools provided by your browser or the relevant advertising platforms.",
    ],
  },
  {
    h: "7. Children's Privacy",
    p: [
      "Our services are intended for adults. We do not knowingly collect personal information from anyone under the age of 18. Information about minor beneficiaries is collected only as provided by a parent or legal guardian for trust preparation purposes.",
    ],
  },
  {
    h: "8. Changes to This Policy",
    p: [
      "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. Your continued use of this website after changes are posted constitutes acceptance of the revised policy.",
    ],
  },
  {
    h: "9. Contact Us",
    p: [
      "If you have questions about this Privacy Policy or how your information is handled, contact us at support@igcfirm.com.",
    ],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-ink min-h-screen">
      <Navbar />
      <main className="pt-32 pb-24" data-testid="privacy-policy-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-gold text-sm tracking-[0.14em] uppercase font-semibold hover:text-goldlight transition-colors duration-300"
          >
            <ArrowLeft size={15} /> Back to Home
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-cream tracking-tight leading-[1.06] mt-8">
            Privacy <em className="text-gold italic">Policy</em>
          </h1>
          <p className="text-cream/50 text-sm mt-4 tracking-wide">Effective date: June 2026 · Iron Gate Consulting, formerly Assets Secure USA</p>

          <div className="mt-8 flex items-start gap-4 bg-surface border border-gold/25 rounded-xl px-6 py-5">
            <ShieldCheck size={20} className="text-gold shrink-0 mt-0.5" />
            <p className="text-cream/80 text-sm leading-relaxed">
              All personal information you provide, including Social Security Numbers, is protected with
              military grade AES 256 GCM encryption.
            </p>
          </div>

          <div className="mt-12 space-y-10">
            {SECTIONS.map((s) => (
              <section key={s.h}>
                <h2 className="font-display text-2xl text-cream">{s.h}</h2>
                {s.p.map((para, i) => (
                  <p key={i} className="text-cream/65 leading-relaxed text-sm sm:text-base mt-4">{para}</p>
                ))}
              </section>
            ))}
          </div>

          <p className="text-cream/40 text-xs leading-relaxed mt-14 pt-8 border-t border-white/8">
            Iron Gate Consulting is not a law firm. Legal review services are provided through veteran
            military benefits. Trust services are provided for estate planning purposes. Consult a licensed
            attorney for legal advice.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
